import { useState, useEffect } from "react";
import { Settings, Save, Eye, EyeOff } from "lucide-react";
import { BACKEND_URL } from "../../context/AuthContext";

const PROVIDER_META = {
  openai: { label: 'OpenAI', color: 'text-green-400', models: ['gpt-4', 'gpt-4-turbo', 'gpt-3.5-turbo', 'gpt-4o'] },
  gemini: { label: 'Google Gemini', color: 'text-blue-400', models: ['gemini-pro', 'gemini-pro-vision', 'gemini-ultra'] },
  anthropic: { label: 'Anthropic', color: 'text-orange-400', models: ['claude-3-opus', 'claude-3-sonnet', 'claude-3-haiku'] },
  local: { label: 'Local / Self-hosted', color: 'text-purple-400', models: ['local-llm'] },
};

export const AISettingsPanel = ({ authToken }) => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showKeys, setShowKeys] = useState({});

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/ai/settings`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (res.ok) setSettings(await res.json());
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const saveSettings = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/ai/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({
          default_provider: settings.default_provider,
          providers: settings.providers,
          max_monthly_spend: settings.max_monthly_spend,
          auto_retry: settings.auto_retry,
          save_history: settings.save_history,
        }),
      });
      if (res.ok) { setSaved(true); setTimeout(() => setSaved(false), 2000); }
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  const updateProvider = (name, key, value) => {
    setSettings(prev => ({
      ...prev,
      providers: {
        ...prev.providers,
        [name]: { ...prev.providers[name], [key]: value },
      },
    }));
  };

  if (loading) return <div className="text-center py-8"><div className="w-6 h-6 border-2 border-white/20 border-t-cyan-500 rounded-full animate-spin mx-auto" /></div>;

  return (
    <div data-testid="ai-settings-panel" className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <Settings className="w-5 h-5 text-cyan-400" /> AI Settings
        </h3>
        <button onClick={saveSettings} disabled={saving}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition ${saved ? 'bg-green-500/20 text-green-400' : 'bg-cyan-600 hover:bg-cyan-500 text-white'}`}
          data-testid="ai-settings-save">
          <Save className="w-4 h-4" /> {saved ? 'Saved!' : saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>

      {/* Default Provider */}
      <div className="bg-white/[0.03] border border-white/[0.08] rounded-xl p-5">
        <label className="block text-white/60 text-sm mb-2">Default Provider</label>
        <select value={settings?.default_provider || 'openai'} onChange={e => setSettings(p => ({ ...p, default_provider: e.target.value }))}
          className="bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-cyan-500/50 w-full max-w-xs"
          data-testid="ai-settings-default-provider">
          {Object.entries(PROVIDER_META).map(([key, meta]) => (
            <option key={key} value={key} className="bg-gray-900">{meta.label}</option>
          ))}
        </select>
      </div>

      {/* Provider Configs */}
      {Object.entries(PROVIDER_META).map(([name, meta]) => {
        const prov = settings?.providers?.[name] || {};
        return (
          <div key={name} className="bg-white/[0.03] border border-white/[0.08] rounded-xl p-5" data-testid={`ai-provider-config-${name}`}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <h4 className={`font-semibold ${meta.color}`}>{meta.label}</h4>
                <button onClick={() => updateProvider(name, 'enabled', !prov.enabled)}
                  className={`px-3 py-1 rounded-lg text-xs font-medium transition ${prov.enabled ? 'bg-green-500/20 text-green-400' : 'bg-white/5 text-white/40'}`}
                  data-testid={`ai-provider-toggle-${name}`}>
                  {prov.enabled ? 'Enabled' : 'Disabled'}
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-white/50 text-xs mb-1">API Key</label>
                <div className="flex gap-2">
                  <input
                    type={showKeys[name] ? 'text' : 'password'}
                    value={prov.api_key || ''}
                    onChange={e => updateProvider(name, 'api_key', e.target.value)}
                    placeholder={prov.has_key ? prov.api_key_masked : 'Enter API key...'}
                    className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm font-mono focus:outline-none focus:border-cyan-500/50"
                    data-testid={`ai-provider-key-${name}`}
                  />
                  <button onClick={() => setShowKeys(p => ({ ...p, [name]: !p[name] }))}
                    className="text-white/30 hover:text-white p-2">
                    {showKeys[name] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-white/50 text-xs mb-1">Default Model</label>
                <select value={prov.default_model || meta.models[0]}
                  onChange={e => updateProvider(name, 'default_model', e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-cyan-500/50"
                  data-testid={`ai-provider-model-${name}`}>
                  {meta.models.map(m => <option key={m} value={m} className="bg-gray-900">{m}</option>)}
                </select>
              </div>
              {name === 'local' && (
                <div className="md:col-span-2">
                  <label className="block text-white/50 text-xs mb-1">Endpoint URL</label>
                  <input type="text" value={prov.endpoint || ''} onChange={e => updateProvider(name, 'endpoint', e.target.value)}
                    placeholder="http://localhost:11434"
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-cyan-500/50" />
                </div>
              )}
            </div>
          </div>
        );
      })}

      {/* General Settings */}
      <div className="bg-white/[0.03] border border-white/[0.08] rounded-xl p-5">
        <h4 className="text-white font-semibold mb-4">General</h4>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-white/60 text-sm">Monthly Spend Limit</span>
            <div className="flex items-center gap-2">
              <span className="text-white/40 text-sm">$</span>
              <input type="number" value={settings?.max_monthly_spend || 50}
                onChange={e => setSettings(p => ({ ...p, max_monthly_spend: parseFloat(e.target.value) || 0 }))}
                className="w-24 bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-white text-sm text-right focus:outline-none focus:border-cyan-500/50"
                data-testid="ai-settings-spend-limit" />
            </div>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-white/60 text-sm">Auto-retry failed jobs</span>
            <button onClick={() => setSettings(p => ({ ...p, auto_retry: !p.auto_retry }))}
              className={`px-3 py-1 rounded-lg text-xs font-medium transition ${settings?.auto_retry ? 'bg-green-500/20 text-green-400' : 'bg-white/5 text-white/40'}`}
              data-testid="ai-settings-auto-retry">
              {settings?.auto_retry ? 'On' : 'Off'}
            </button>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-white/60 text-sm">Save prompt history</span>
            <button onClick={() => setSettings(p => ({ ...p, save_history: !p.save_history }))}
              className={`px-3 py-1 rounded-lg text-xs font-medium transition ${settings?.save_history ? 'bg-green-500/20 text-green-400' : 'bg-white/5 text-white/40'}`}
              data-testid="ai-settings-save-history">
              {settings?.save_history ? 'On' : 'Off'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
